import { useEffect, useState } from 'react';

import { audioProcessor } from '../services/AudioProcessorService';

import { AudioPreset, getRecommendedPreset } from '../utils/audio-optimizer';

import { useMediaSettingsStore, useRoomActionsStore, useRoomStore } from '../stores';

export function useNoiseCancellation() {
  // Initialize with the current actual service state
  const [activePreset, setActivePreset] = useState<AudioPreset>(audioProcessor.currentPreset);
  const [vadLevel, setVadLevel] = useState<number | undefined>(undefined);
  const [smartMixingEnabled, setSmartMixingEnabled] = useState<boolean>(audioProcessor.smartMixingEnabled);
  const [dynamicSuppressionEnabled, setDynamicSuppressionEnabled] = useState<boolean>(
    audioProcessor.dynamicSuppressionEnabled
  );
  const [suppressionLevel, setSuppressionLevel] = useState<number>(audioProcessor.suppressionLevel);
  const [effectiveLevel, setEffectiveLevel] = useState<number | undefined>(undefined);

  // Detailed Component States
  const [preGainEnabled, setPreGainEnabled] = useState(true);
  const [eqEnabled, setEQEnabled] = useState(true);
  const [compressorEnabled, setCompressorEnabled] = useState(true);
  const [gateEnabled, setGateEnabled] = useState(true);
  const [postGainEnabled, setPostGainEnabled] = useState(true);
  const [echoCancellationEnabled, setEchoCancellationEnabled] = useState<boolean>(
    audioProcessor.echoCancellationEnabled
  );

  // Handlers
  const changePreset = async (preset: AudioPreset) => {
    console.log('[useNoiseCancellation] Changing preset to:', preset);
    audioProcessor.setPreset(preset);
    setActivePreset(preset);
    // setVadLevel(undefined); // Reset VAD level to prevent stale DeepFilter values from blocking RNNoise metrics

    const roomClient = (window as any).roomClient;
    const { isJoinedRoom } = useRoomStore.getState();

    if (isJoinedRoom && roomClient) {
      const currentDeviceId = useMediaSettingsStore.getState().mediaSettings.selectedAudioInputDevice?.deviceId;
      const isMicClosed = useRoomActionsStore.getState().micSettings.isMicClosed;
      if (!isMicClosed) {
        console.log('[useNoiseCancellation] Restarting audio producer for new preset...');
        // Use atomic restart to apply new worklet/preset
        await roomClient.atomicCloseThenProduce('audioType', currentDeviceId);
        useRoomActionsStore.getState().setMicSettings({ isMicClosed: false });
      }
    }
  };

  const toggleSmartMixing = (enabled: boolean) => {
    console.log('[useNoiseCancellation] Toggling Smart Mixing:', enabled);
    audioProcessor.setSmartMixing(enabled);
    setSmartMixingEnabled(enabled);
  };

  const toggleDynamicSuppression = (enabled: boolean) => {
    console.log('[useNoiseCancellation] Toggling Dynamic Suppression:', enabled);
    audioProcessor.setDynamicSuppression(enabled);
    setDynamicSuppressionEnabled(enabled);
  };

  const changeSuppressionLevel = (level: number) => {
    audioProcessor.setSuppressionLevel(level);
    setSuppressionLevel(level);
  };

  /**
   * Toggle echo cancellation and restart audio producer to apply new constraints.
   * When disabled (default), avoids AEC+DeepFilter double processing for cleaner audio.
   * Enable when using speakers (not headphones) to prevent echo feedback.
   */
  const toggleEchoCancellation = async (enabled: boolean) => {
    console.log('[useNoiseCancellation] Toggling Echo Cancellation:', enabled);
    audioProcessor.setEchoCancellation(enabled);
    setEchoCancellationEnabled(enabled);

    // Restart audio producer to apply new constraints in real-time
    const roomClient = (window as any).roomClient;
    const { isJoinedRoom } = useRoomStore.getState();

    if (isJoinedRoom && roomClient) {
      const currentDeviceId = useMediaSettingsStore.getState().mediaSettings.selectedAudioInputDevice?.deviceId;
      const isMicClosed = useRoomActionsStore.getState().micSettings.isMicClosed;
      if (!isMicClosed) {
        console.log('[useNoiseCancellation] Restarting audio producer for echo cancellation change...');
        await roomClient.atomicCloseThenProduce('audioType', currentDeviceId);
        useRoomActionsStore.getState().setMicSettings({ isMicClosed: false });
      }
    }
  };

  // Subscribe to VAD updates
  // useEffect(() => {
  //   return audioProcessor.onVad((level) => setVadLevel(level));
  // }, []);

  useEffect(() => {
    return audioProcessor.onMetrics((metrics) => {
      if (metrics.effectiveSuppressionLevel !== undefined) {
        setEffectiveLevel(metrics.effectiveSuppressionLevel);
      }
    });
  }, []);

  return {
    processStream: (stream: MediaStream) => audioProcessor.processStream(stream),
    setPreset: changePreset,
    activePreset,
    recommendedPreset: getRecommendedPreset(),
    isReady: true,
    workletNode: audioProcessor.node,
    vadLevel,
    isDeepFilterActive: activePreset === 'high',
    smartMixingEnabled,
    setSmartMixing: toggleSmartMixing,
    dynamicSuppressionEnabled,
    setDynamicSuppression: toggleDynamicSuppression,
    suppressionLevel,
    setSuppressionLevel: changeSuppressionLevel,
    effectiveSuppressionLevel: effectiveLevel,
    // Individual Component Toggles
    preGainEnabled,
    setPreGainEnabled: (e: boolean) => {
      audioProcessor.setPreGainEnabled(e);
      setPreGainEnabled(e);
    },
    eqEnabled,
    setEQEnabled: (e: boolean) => {
      audioProcessor.setEQEnabled(e);
      setEQEnabled(e);
    },
    compressorEnabled,
    setCompressorEnabled: (e: boolean) => {
      audioProcessor.setCompressorEnabled(e);
      setCompressorEnabled(e);
    },
    gateEnabled,
    setGateEnabled: (e: boolean) => {
      audioProcessor.setGateEnabled(e);
      setGateEnabled(e);
    },
    postGainEnabled,
    setPostGainEnabled: (e: boolean) => {
      audioProcessor.setPostGainEnabled(e);
      setPostGainEnabled(e);
    },
    // Echo Cancellation
    echoCancellationEnabled,
    setEchoCancellation: toggleEchoCancellation,
  };
}
